// `arthelper-dev.com` hosts the non-production stages (`artists-<stage>.arthelper-dev.com`,
// infra/NextjsLayer.ts). It is first-party, and its brand-prefixed label reads as an
// impersonation lookalike to spam scoring unless it is listed here.
const INTERNAL_HOST_SUFFIXES = ['arthelper.com', 'arthelper.ai', 'arthelper-dev.com'];

/** Hosts allowed for tool-rendered `<img src>` (S3/CDN + first-party). */
const TRUSTED_TOOL_MEDIA_HOST_SUFFIXES = [...INTERNAL_HOST_SUFFIXES, 'amazonaws.com', 'cloudfront.net'];

/** True for first-party ArtHelper hosts, including subdomains (`www.`, `app.`, `staging.`). */
export function isInternalHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase();
  return INTERNAL_HOST_SUFFIXES.some((suffix) => normalized === suffix || normalized.endsWith(`.${suffix}`));
}

function isTrustedToolMediaHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase();
  return TRUSTED_TOOL_MEDIA_HOST_SUFFIXES.some((suffix) => normalized === suffix || normalized.endsWith(`.${suffix}`));
}

/** Local dev serves S3 objects from the API at /local-storage/{bucket}/{key}. */
function allowsLocalDevToolMedia(): boolean {
  if (typeof process !== 'undefined' && (process.env.NODE_ENV === 'test' || process.env.VITEST === 'true')) {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const host = window.location.hostname.toLowerCase();
  return host === 'localhost' || host === '127.0.0.1';
}

function isLocalFirstPartyStorageUrl(parsed: URL): boolean {
  if (!allowsLocalDevToolMedia()) {
    return false;
  }

  const host = parsed.hostname.toLowerCase();
  if (host !== 'localhost' && host !== '127.0.0.1') {
    return false;
  }

  return parsed.pathname.includes('/local-storage/');
}

function isTrustedToolMediaPath(pathname: string): boolean {
  return allowsLocalDevToolMedia() && pathname.includes('/local-storage/');
}

/**
 * Returns rel tokens for user-supplied profile links.
 * External http(s) URLs get nofollow ugc noopener noreferrer; internal ArtHelper URLs and relative paths get none.
 */
export function getLinkRelAttribute(href: string): string {
  if (typeof href !== 'string') {
    return '';
  }

  const trimmed = href.trim();
  if (!trimmed) {
    return '';
  }

  if (trimmed.startsWith('/') || trimmed.startsWith('./') || trimmed.startsWith('../') || trimmed.startsWith('#')) {
    return '';
  }

  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed) && !/^https?:\/\//i.test(trimmed)) {
    return '';
  }

  try {
    const url = new URL(trimmed.startsWith('//') ? `https:${trimmed}` : trimmed);

    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return '';
    }

    if (isInternalHostname(url.hostname)) {
      return '';
    }

    return 'nofollow ugc noopener noreferrer';
  } catch {
    return '';
  }
}

/**
 * Returns the href only when it is a safe http(s) URL, otherwise null.
 * Defense-in-depth for user-supplied links so active schemes (javascript:, data:, vbscript:, …)
 * can never reach an anchor href even if they bypass write-time validation.
 */
export function getSafeExternalHref(href: string | null | undefined): string | null {
  if (typeof href !== 'string') {
    return null;
  }

  const trimmed = href.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const url = new URL(trimmed.startsWith('//') ? `https:${trimmed}` : trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:' ? trimmed : null;
  } catch {
    return null;
  }
}

/** data:image/<subtype>[;params],<payload> — for <img src> only, never anchor hrefs. */
const SAFE_DATA_IMAGE_SRC = /^data:image\/[a-z0-9.+-]+(?:;[^,]*)?,/i;

/**
 * Returns a URL safe for use as an `<img src>`.
 * Allows http(s) and `data:image/*` (validated media-type prefix). Rejects
 * javascript:/vbscript:/other data: schemes. Do NOT use for anchor hrefs —
 * keep those on `getSafeExternalHref`.
 */
export function getSafeImageSrc(src: string | null | undefined): string | null {
  if (typeof src !== 'string') {
    return null;
  }

  const trimmed = src.trim();
  if (!trimmed) {
    return null;
  }

  if (SAFE_DATA_IMAGE_SRC.test(trimmed)) {
    return trimmed;
  }

  return getSafeExternalHref(trimmed);
}

/**
 * Returns a URL safe for tool-rendered `<img src>`: http(s) on trusted hosts only,
 * plus validated `data:image/*`. Rejects arbitrary third-party origins to prevent
 * browser-side request forgery from attacker-influenced tool output.
 */
export function getTrustedToolMediaUrl(url: string | null | undefined): string | null {
  if (typeof url !== 'string') {
    return null;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.startsWith('/') && isTrustedToolMediaPath(trimmed)) {
    return trimmed;
  }

  const safe = getSafeImageSrc(url);
  if (!safe) {
    return null;
  }

  if (SAFE_DATA_IMAGE_SRC.test(safe)) {
    return safe;
  }

  try {
    const parsed = new URL(safe.startsWith('//') ? `https:${safe}` : safe);
    if (isTrustedToolMediaHostname(parsed.hostname) || isLocalFirstPartyStorageUrl(parsed)) {
      return safe;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Returns rel tokens for profile outbound links based on subscription tier.
 * Paid users (pro/growth) get dofollow links; free users get nofollow.
 */
export function getSubscriptionLinkRelAttribute(subscription: string | null | undefined): string {
  if (subscription === 'pro' || subscription === 'growth') {
    return 'noopener noreferrer';
  }
  return 'nofollow noopener noreferrer';
}

/**
 * The only URL template variable a link-out spark supports. Matched literally,
 * so the admin form ships this exact string as help text.
 */
export const IMAGE_ID_TOKEN = '{{ image_id }}';

/**
 * Resolves a link-out spark's URL template for one artwork.
 *
 * Surfaces with no per-artwork context (the daily community sparklink card)
 * pass `null`, which drops the token rather than leaving literal template text
 * in the navigated URL.
 */
export function substituteImageId(urlTemplate: string, imageId: string | null): string {
  return urlTemplate.split(IMAGE_ID_TOKEN).join(imageId ?? '');
}

/**
 * True when a link-out spark's URL template is structurally usable: it resolves
 * to an http(s) URL both with `{{ image_id }}` substituted and with it dropped,
 * and carries no other `{{ … }}` variable.
 *
 * The leftover-brace check is what catches a near-miss like `{{image_id}}`, which
 * substitutes to nothing, still parses as a URL, and would be percent-encoded
 * into the destination instead of replaced. Shared by the admin form and the
 * write-time validator so the two cannot drift.
 */
export function isLinkOutUrlTemplate(urlTemplate: string): boolean {
  const substitutions = [substituteImageId(urlTemplate, '12345'), substituteImageId(urlTemplate, null)];

  return substitutions.every(
    (substitution) =>
      !substitution.includes('{{') && !substitution.includes('}}') && !!getSafeExternalHref(substitution)
  );
}
